export interface FilterValue {
	value: string;
	label: string;
	count?: number;
	selected: boolean;
}

interface FilterGroupBase {
	id: string;
	/** A group with no label shows under the group above it, or alone at the top. */
	label?: string;
}

export interface ListFilterGroup extends FilterGroupBase {
	kind: "list";
	values: FilterValue[];
	maxShown?: number;
}

export interface ToggleFilterGroup extends FilterGroupBase {
	kind: "toggle";
	value: FilterValue;
}

export interface FilterRange {
	min?: number;
	max?: number;
}

export interface RangeFilterGroup extends FilterGroupBase {
	kind: "range";
	bounds: { min: number; max: number };
	selected: FilterRange;
	locale?: string;
	formatOptions?: Intl.NumberFormatOptions;
}

export type FilterGroup =
	ListFilterGroup | ToggleFilterGroup | RangeFilterGroup;

export interface FilterValueRef {
	groupId: string;
	value: string;
}

export interface FilterValueChange extends FilterValueRef {
	selected: boolean;
}

export interface FilterRangeChange extends FilterRange {
	groupId: string;
}

export interface AppliedFilter extends FilterValueRef {
	label: string;
}

export interface SortOption {
	value: string;
	label: string;
	/** Fits the sort button on a phone, e.g. "Price ↑". Defaults to `label`. */
	shortLabel?: string;
}

export type SortControlPresentation = "menu" | "sheet";

export type FilterPanelPresentation = "inline" | "drawer";

export const RANGE_MIN = "min";
export const RANGE_MAX = "max";

export function isFilterGroupActive(group: FilterGroup): boolean {
	switch (group.kind) {
		case "list":
			return group.values.length > 0;
		case "toggle":
			return true;
		case "range":
			return (
				group.bounds.max > group.bounds.min || hasRange(group.selected)
			);
	}
}

function hasRange(range: FilterRange): boolean {
	return range.min !== undefined || range.max !== undefined;
}

function rangeChips(group: RangeFilterGroup): AppliedFilter[] {
	const format = new Intl.NumberFormat(group.locale, group.formatOptions);
	const ends = [
		{ value: RANGE_MIN, name: "Min", amount: group.selected.min },
		{ value: RANGE_MAX, name: "Max", amount: group.selected.max }
	];
	return ends.flatMap(({ value, name, amount }) =>
		amount === undefined
			? []
			: [
					{
						groupId: group.id,
						value,
						label: `${name} ${format.format(amount)}`
					}
				]
	);
}

function groupChips(group: FilterGroup): AppliedFilter[] {
	switch (group.kind) {
		case "list":
			return group.values
				.filter((value) => value.selected)
				.map((value) => ({
					groupId: group.id,
					value: value.value,
					label: value.label
				}));
		case "toggle":
			return group.value.selected
				? [
						{
							groupId: group.id,
							value: group.value.value,
							label: group.value.label
						}
					]
				: [];
		case "range":
			return rangeChips(group);
	}
}

export function selectAppliedFilters(groups: FilterGroup[]): AppliedFilter[] {
	const firstLabelled = groups.findIndex(
		(group) => group.label !== undefined
	);
	const quickFilters =
		firstLabelled === -1 ? groups : groups.slice(0, firstLabelled);
	const rest = groups.slice(quickFilters.length);
	return [
		...quickFilters,
		...rest.filter((group) => group.kind === "range"),
		...rest.filter((group) => group.kind !== "range")
	].flatMap(groupChips);
}
